"use client";

import React, { useEffect, useCallback, useSyncExternalStore } from 'react';
import Link from 'next/link';
import { Play, Pause, ExternalLink, Download } from 'lucide-react';
import { playToggleA11y } from '@/lib/a11y/play-toggle';
import { Spinner } from '@/components/ui/spinner';
import { useAudioPlayerContext } from '@/contexts/audio-player-context';
import { useAudioUrl } from '@/hooks/useAudioUrl';
import { type Sound } from '@/lib/supabase';
import { AudioWaveformCompact } from '@/components/ui/audio-waveform';
import { soundUrl } from '@/lib/slugify';
import { audioPreloader } from '@/lib/audio-preloader';

// Hydration-safe mounted check
const subscribe = () => () => {};
const getSnapshot = () => true;
const getServerSnapshot = () => false;

interface BlogSoundEmbedProps {
  sound: Sound;
  className?: string;
}

/**
 * Compact inline audio player for embedding in blog content.
 * Features:
 * - Play/pause button with loading state
 * - Sound title linked to detail page (opens in new tab)
 * - Compact waveform visualization
 * - Duration display
 */
export const BlogSoundEmbed: React.FC<BlogSoundEmbedProps> = ({
  sound,
  className = '',
}) => {
  // Hydration-safe mounted check using useSyncExternalStore
  const mounted = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Global audio player context
  const globalPlayer = useAudioPlayerContext();

  // Check if this sound is currently playing
  const isThisSoundPlaying = globalPlayer.currentSound?.id === sound?.id && globalPlayer.isPlaying;
  const isThisSoundLoading = globalPlayer.currentSound?.id === sound?.id && globalPlayer.isLoading;

  // Audio URL resolution + preloading. The sound carries a server-resolved
  // audioUrl (TES-831) when it came from /api/sounds, so no client-side
  // storageApi resolution is needed.
  const { audioUrl } = useAudioUrl(sound?.file_url || null, sound?.audioUrl ?? null);

  // Preload audio data when URL is resolved
  useEffect(() => {
    if (audioUrl && sound?.id) {
      audioPreloader.preload(sound.id, audioUrl);
    }
  }, [audioUrl, sound?.id]);

  // Play/Pause handler
  // eslint-disable-next-line react-doctor/no-event-handler -- handlePlayPause is a direct DOM event handler; naming is correct
  const handlePlayPause = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mounted) return;
    // Loading counts as "engaged" so a second activation during the load
    // cancels it rather than re-issuing play() — the control is no longer
    // `disabled` while loading (TES-1581), matching the detail-page player.
    if (isThisSoundPlaying || isThisSoundLoading) {
      globalPlayer.pause();
    } else {
      globalPlayer.play(sound);
    }
  }, [isThisSoundPlaying, isThisSoundLoading, globalPlayer, sound, mounted]);

  // Format duration
  const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format category name with proper capitalization
  const formatCategory = (category: string): string => {
    // Handle special cases that should stay as-is
    if (category.includes('&') || category.includes(' ')) {
      // Already formatted like "Movies & TV"
      return category;
    }
    // Capitalize first letter of each word
    return category
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (!sound || !sound.id) {
    return null;
  }

  return (
    <div
      className={`
        group relative flex items-center gap-3 py-3
        border-y border-[color:var(--tls-border-subtle)]
        transition-[background-color,border-color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]
        hover:border-[color:color-mix(in_srgb,var(--tls-brand)_28%,var(--tls-border-subtle))]
        ${isThisSoundPlaying ? 'border-[color:color-mix(in_srgb,var(--tls-brand)_34%,var(--tls-border-subtle))] bg-[color-mix(in_srgb,var(--tls-brand)_8%,var(--tls-surface))]' : ''}
        ${className}
      `}
    >
      {/* Play/Pause Button */}
      <button
        type="button"
        onClick={handlePlayPause}
        // NOT `disabled` while loading — a disabled element is blurred by the
        // browser, so a keyboard user would lose focus and never hear the
        // resulting state change (TES-1579 / TES-1581). `aria-busy` conveys
        // buffering instead, and `[aria-busy]` carries the dimmed styling.
        className={`
          flex-shrink-0 w-10 h-10 rounded-[var(--radius-pill)]
          flex items-center justify-center
          transition-[background-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]
          ${isThisSoundPlaying
            ? 'bg-[var(--tls-brand)] text-[var(--tls-brand-fg)]'
            : 'bg-[var(--tls-surface-elevated)] text-[var(--tls-fg-strong)] hover:bg-[var(--tls-brand)] hover:text-[var(--tls-brand-fg)]'
          }
          aria-[busy=true]:opacity-50 aria-[busy=true]:cursor-wait
        `}
        // Playback state is exposed via aria-pressed with a stable name (TES-1581).
        {...playToggleA11y({
          playing: isThisSoundPlaying,
          title: sound.title,
          loading: isThisSoundLoading,
        })}
      >
        {isThisSoundLoading ? (
          <Spinner className="size-4" />
        ) : isThisSoundPlaying ? (
          <Pause className="size-4" fill="currentColor" />
        ) : (
          <Play className="size-4 ml-0.5" fill="currentColor" />
        )}
      </button>

      {/* Title & Link */}
      <div className="flex-1 min-w-0">
        <Link
          href={soundUrl(sound)}
          target="_blank"
          rel="noopener noreferrer"
          className="
            group/link flex items-center gap-1.5
            text-[var(--tls-fg-strong)] font-medium text-sm hover:text-[var(--tls-brand-ink)]
            transition-colors truncate
          "
        >
          <span className="truncate">{sound.title}</span>
          <ExternalLink className="size-3 flex-shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" />
        </Link>
        {sound.category && (
          <p className="text-xs text-[var(--tls-fg-subtle)] mt-0.5 truncate">
            {formatCategory(sound.category)}
          </p>
        )}
      </div>

      {/* Waveform */}
      {mounted && (
        <div className="hidden sm:block w-24 flex-shrink-0">
          <AudioWaveformCompact
            soundId={sound.id}
            waveformData={sound.waveform_data}
            progress={isThisSoundPlaying ? globalPlayer.progress : 0}
            isPlaying={isThisSoundPlaying}
            isLoading={isThisSoundLoading}
          />
        </div>
      )}

      {/* Duration */}
      <span className="flex-shrink-0 text-xs font-semibold text-[var(--tls-fg-subtle)] font-sans tabular-nums">
        {formatDuration(sound.duration)}
      </span>

      {/* Download — links to the sound's detail page rather than duplicating
          the download state machine here. */}
      <Link
        href={soundUrl(sound)}
        aria-label={`Get ${sound.title}`}
        className="
          flex flex-shrink-0 min-h-[44px] min-w-[44px] items-center justify-center
          rounded-[var(--radius-pill)] text-[var(--tls-fg-subtle)]
          transition-colors duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]
          hover:bg-[var(--tls-surface-elevated)] hover:text-[var(--tls-brand)]
        "
      >
        <Download className="size-4" aria-hidden="true" />
      </Link>
    </div>
  );
};

/**
 * Loading skeleton for BlogSoundEmbed
 */
export const BlogSoundEmbedSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`flex items-center gap-3 py-3 border-y border-[color:var(--tls-border-subtle)] ${className}`}>
      <div className="size-10 rounded-[var(--radius-pill)] bg-[var(--tls-surface-elevated)]" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 rounded-[var(--ls-radius-0)] bg-[var(--tls-surface-elevated)]" />
        <div className="h-3 w-20 rounded-[var(--ls-radius-0)] bg-[color-mix(in_srgb,var(--tls-surface-elevated)_72%,transparent)]" />
      </div>
      <div className="hidden sm:block w-24 h-8 rounded-[var(--ls-radius-0)] bg-[color-mix(in_srgb,var(--tls-surface-elevated)_72%,transparent)]" />
      <div className="w-10 h-4 rounded-[var(--ls-radius-0)] bg-[color-mix(in_srgb,var(--tls-surface-elevated)_72%,transparent)]" />
    </div>
  );
};
